import { useEffect, useState, type FC } from "react";
import { type ContainsStringValueFilter } from "remult";
import { CircularProgress, Fade, Tooltip } from "@mui/material";
import { Stack } from "@mui/material";
import { format } from "date-fns";

import { opportunityRepo, opportunityReportRepo, timeReportRepo } from "@repositories";
import { type Association, Opportunity, OpportunityReport } from "@entities";

import { formatOpportunitySchedule } from "@shared/helpers/format-opportunity-schedule";
import { useOrganization } from "../../../contexts/OrganizationProvider";
import { EventDayDate } from "../../event-day-date/EventDayDate";

import "./UserVolunteering.scss";

export const UserVolunteering: FC<{ association: Partial<Association> | undefined; }> = ({
  association,
}) => {
  const [averageRating, setAverageRating] = useState<number>(0);
  const [volunteerHours, setVolunteerHours] = useState<number>(0);
  const [userTimeReports, setUserTimeReports] = useState<number>(0);
  const [events, setEvents] = useState<Opportunity[]>([]);
  const [noTimeEvents, setNoTimeEvents] = useState<Opportunity[]>([]);
  const [eventsByMonthYear, setEventsByMonthYear] =
    useState<Record<string, Opportunity[]>>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { currOrganization } = useOrganization();

  useEffect(() => {
    setIsLoading(true);
    if (association) {
      getUsersReviews();
      getVolunteersData();
      if (currOrganization?.hasHoursReporting) {
        getUserTimeReports();
      }
    }
  }, [association]);

  async function getUsersReviews() {
    const reviews = await opportunityReportRepo.find({
      where: OpportunityReport.hyperFilter({
        association: {
          $is: {
            id: association?.id,
            organizationId: currOrganization!.id,
          }
        }
      }),
    });
    // Rating field doesn't exist in OpportunityReport, using isGoalAchieved as indicator
    const ratings = (reviews || []).map((review) => review.isGoalAchieved ? 5 : 0);
    const averageRating = Number(
      (
        (ratings || []).reduce((sum: number, rating) => sum + Number(rating || 0), 0) /
        ((ratings || []).length || 1)
      ).toFixed(1),
    );
    setAverageRating(averageRating);
  }

  async function getUserTimeReports() {
    const userHoursReports = await timeReportRepo.find({
      where: {
        association: { id: association?.id || "", organizationId: currOrganization!.id } as ContainsStringValueFilter,
      },
    });
    const hours = userHoursReports.map((report) => report.hourCount);
    const totalHours = hours.reduce((acc, curr) => acc + curr, 0);
    setUserTimeReports(Number(totalHours.toFixed(2)));
  }

  async function getVolunteersData() {
    const opportunities = await opportunityRepo.find({
      where: Opportunity.hyperFilter({
        participantUsers: {
          $some: {
            association: {
              $is: {
                id: association?.id,
                organizationId: currOrganization!.id,
              },
            },
          },
        },
      }),
      orderBy: { startDate: "desc" },
    });
    const eventsByMonthYear = opportunities?.reduce(
      (acc, event) => {
        if (event?.startDate) {
          const monthYearKey = format(new Date(event.startDate), "MM-yyyy");
          if (!acc[monthYearKey]) {
            acc[monthYearKey] = [];
          }
          acc[monthYearKey].push(event);
        }
        return acc;
      },
      {} as Record<string, Opportunity[]>,
    );
    setEventsByMonthYear(eventsByMonthYear);
    let totalTime = 0;
    opportunities?.forEach((opportunity) => {
      const opportunityTime =
        (Number(opportunity?.endDate) - Number(opportunity?.startDate)) /
        1000 /
        60 /
        60;
      if (opportunityTime < 0) return;
      totalTime += opportunityTime;
    });
    setVolunteerHours((Math.round(totalTime * 100) / 100));
    setNoTimeEvents(
      opportunities.filter((ev) => !!ev && !ev.hasTime) as Opportunity[] || [],
    );
    setEvents(opportunities.filter(ev => !!ev) as Opportunity[] || []);
    setIsLoading(false);
  }

  const renderNoEvents = () => (
    <Fade in={true} timeout={800}>
      <div className="no-events-message">
        <p>לא נמצאו התנדבויות עבור המשתמש</p>
      </div>
    </Fade>
  );

  return (
    <>
      {isLoading
        ? <Stack width="100%" height="100%" justifyContent="center" alignItems="center">
          <CircularProgress />
        </Stack>
        : <div className="volunteering-container">
          <div className="volunteer-stats-container">
            <Tooltip title="שעות התנדבות כוללות" arrow placement="top">
              <Stack className="volunteer-stat" width="32%">
                <img src="clock.svg" alt="שעות התנדבות" />
                <div className="details">
                  <p className="number">{volunteerHours ? volunteerHours : "-"}</p>
                  <p>שעות התנדבות {currOrganization?.hasHoursReporting ? `(${userTimeReports} אושרו)` : ""}</p>
                </div>
              </Stack>
            </Tooltip>
            <Tooltip title="מספר ההתנדבויות של המשתמש" arrow placement="top">
              <Stack className="volunteer-stat" width="32%">
                <img src="3hands.svg" alt="מספר התנדבויות" />
                <div className="details">
                  <p className="number">{events.length || "-"}</p>
                  <p>מספר התנדבויות</p>
                </div>
              </Stack>
            </Tooltip>
            <Tooltip title="דירוג ממוצע מהתנדבויות קודמות" arrow placement="top">
              <Stack className="volunteer-stat" width="32%">
                <img src="ratings.svg" alt="דירוג ממוצע" />
                <div className="details">
                  <p className="number">{averageRating || "-"}</p>
                  <p>דירוג ממוצע</p>
                </div>
              </Stack>
            </Tooltip>
          </div>
          <Fade in={true} timeout={600}>
            <div className="last-entry">
              <img src="message.svg" alt="כניסה אחרונה" />
              <p>
                {association?.user?.lastEntry
                  ? `כניסה אחרונה ב- ${association?.user?.lastEntry.toLocaleDateString("he")}`
                  : "עוד לא התבצעה כניסה למערכת"}
              </p>
            </div>
          </Fade>
          <div className="user-events-container">
            {noTimeEvents.length > 0 && (
              <div className="month-events-container">
                <p className="month-year">ללא זמן מוגדר</p>
                <div className="events-container">
                  {noTimeEvents.map((event) => (
                    <div className="event-details" key={event.id}>
                      <EventDayDate
                        date={undefined}
                        fallbackLabel={formatOpportunitySchedule(event)}
                        participants={event.participantsCount || 0}
                        amount={event.amount || 1}
                        id={event.id}
                        isFull={event.isFull}
                      />
                      <p className="par">{event.title}</p>
                      <p className="par">{formatOpportunitySchedule(event)}</p>
                      <p className="par">{event.location}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {!eventsByMonthYear || Object.keys(eventsByMonthYear).length === 0
              ? noTimeEvents.length === 0 && renderNoEvents()
              : Object.entries(eventsByMonthYear || {}).map(([monthYear, events]) => (
                <div className="month-events-container" key={monthYear}>
                  <p className="month-year">{monthYear}</p>
                  <div className="events-container">
                    {events.map((event, index) => (
                      <div className="event-details" key={index}>
                        <EventDayDate
                          date={new Date(event.startDate || "")}
                          participants={event.participantsCount || 0}
                          amount={event.amount || 1}
                          id={event.id}
                          isFull={event.isFull}
                        />
                        <p className="par">{event.title}</p>
                        <p className="par">
                          {formatOpportunitySchedule(event)}
                        </p>
                        <p className="par">{event.location}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
          </div>
        </div>}
    </>
  );
};
